import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import getCriteriaArguments from './criteria-arguments';
import { popularFilterByOptions, otherFilterByOptions } from './filter-options';
import { REFUND_ASSIST_VIEW_ID, VOIDED_VIEW_ID } from '../../../components/shipping-grid/views/view-id-constants';
import { criteriaOperatorsMap } from '../../../containers/base-filter-bar-container/criteria-operators';
import { serviceCollectionsNameCorrection } from '../../../containers/base-filter-bar-container/reference-data-criteria-arguments';
import { SERVICE_COLLECTION, REFUND_STATUS, VOID_TYPE } from '../../../filters/constants';
import { useShouldShowVoidType } from '../../../hooks/refund-assist/use-should-show-void-type';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { useInventoryTrackingEnabled, useBTEmailExperienceNew } from '../../../hooks/use-seller-setting';
import { useSortedTranslatedCountries } from '../../../hooks/use-sorted-translated-countries';
import Global from '../../../store/global-selectors';
import { useSelectedShippingViewId } from '../shipping-left-nav/shipping-left-nav-context';

const allOptions = [...popularFilterByOptions, ...otherFilterByOptions];
const baseImplicitFilterByIds = ['shipDate'];
const EMAIL_FILTER_IDS = ['deliveryNotification', 'shipmentNotification'];

const otherOptionsSelector = createSelector(
  (isInventoryEnabled) => isInventoryEnabled,
  (_, isUserLocked) => isUserLocked,
  (_, __, showRateShopper) => showRateShopper,
  (isInventoryEnabled, isUserLocked, showRateShopper) => {
    let filteredOptions = otherFilterByOptions;

    filteredOptions = isInventoryEnabled ? filteredOptions : filteredOptions.filter((filter) => !filter.isInventoryFilter);

    filteredOptions = isUserLocked ? filteredOptions : filteredOptions.filter((filter) => filter.id !== 'assignedTo');

    filteredOptions = showRateShopper ? filteredOptions : filteredOptions.filter((filter) => filter.id !== SERVICE_COLLECTION);

    return filteredOptions;
  },
);

const implicitFilterByIdsSelector = createSelector(
  (isUserLocked) => isUserLocked,
  (isUserLocked) => {
    if (!isUserLocked) {
      return baseImplicitFilterByIds;
    }
    return [...baseImplicitFilterByIds, 'assignedTo'];
  },
);

export const useShipmentsFilterBarState = () => {
  const criteriaArgumentsMap = useSelector(getCriteriaArguments);
  const intl = useIntl();
  const countries = useSortedTranslatedCountries({ labelProp: 'displayName' });
  const correctedCriteriaArgumentsMap = useMemo(() => {
    const correctedServiceCollections = serviceCollectionsNameCorrection({
      intl,
      serviceCollections: criteriaArgumentsMap?.serviceCollection,
    });
    const correctedCounties = countries.map((country) => ({
      ...country,
      id: 'option-country',
      value: country.code,
    }));
    return {
      ...criteriaArgumentsMap,
      serviceCollection: correctedServiceCollections,
      country: correctedCounties,
    };
  }, [intl, criteriaArgumentsMap, countries]);
  const currentFilterView = useSelector(Global.selectShipmentsFilteringActiveFilterView);
  const { appliedFilters } = currentFilterView;
  const isInventoryEnabled = useInventoryTrackingEnabled();
  const isUserLocked = useSelector(Global.selectUseruserHasShipmentViewRestriction);
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const otherFilterBys = otherOptionsSelector(isInventoryEnabled, isUserLocked, showRateShopper);
  const implicitFilterByIds = implicitFilterByIdsSelector(isUserLocked);
  const enableNewEmailExperience = useBTEmailExperienceNew();
  const statusViewId = useSelectedShippingViewId();
  const isRefundAssistView = statusViewId === REFUND_ASSIST_VIEW_ID;
  const isVoidedView = statusViewId === VOIDED_VIEW_ID;
  const shouldShowVoidTypeFilter = useShouldShowVoidType();

  const filteredOptions = useMemo(() => {
    return allOptions.filter((option) => {
      if (option.id === SERVICE_COLLECTION) {
        return showRateShopper;
      }

      if (enableNewEmailExperience && EMAIL_FILTER_IDS.includes(option.id)) {
        return false;
      }

      // Only show refund status filter in Refund Assist view
      if (option.id === REFUND_STATUS) {
        return isRefundAssistView;
      }

      // Only show void type filter in Voided view
      if (option.id === VOID_TYPE) {
        return isVoidedView && shouldShowVoidTypeFilter;
      }

      return true;
    });
  }, [enableNewEmailExperience, showRateShopper, isRefundAssistView, isVoidedView, shouldShowVoidTypeFilter]);

  const filteredPopularFilterBys = popularFilterByOptions.filter((option) => {
    // Only show refund status filter in Refund Assist view
    if (option.id === REFUND_STATUS) {
      return isRefundAssistView;
    }
    // Only show void type filter in Voided view
    if (option.id === VOID_TYPE) {
      return isVoidedView && shouldShowVoidTypeFilter;
    }
    return true;
  });

  return {
    allFilterBys: filteredOptions,
    appliedFilters,
    criteriaArgumentsMap: correctedCriteriaArgumentsMap,
    criteriaOperatorsMap,
    currentFilterView,
    implicitFilterByIds,
    otherFilterBys,
    popularFilterBys: filteredPopularFilterBys,
    filterViewId: currentFilterView.filterViewId,
  };
};
